import { useRef, useEffect, useState } from 'react';

import Head from 'design-system/Head';
import {
  getLookupCustomerAuditHistoryProps,
  getLookupInternalAuditHistoryProps,
} from 'react-scale/corp/components/Lookup/BaseLookupObject';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import { Extras } from 'react-scale/corp/components/Lookup/LookupTypes';
import CustomerAuditHistory from 'react-scale/corp/components/Lookup/sections/CustomerAuditHistory';
import InternalAuditHistory from 'react-scale/corp/components/Lookup/sections/InternalAuditHistory';
import {
  PipelineNodeGraph,
  PipelineHistoryGraph,
} from 'react-scale/corp/components/Lookup/sections/PipelineV3Graphs';
import HideableSection from 'react-scale/corp/components/UI/HideableSection';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';
import { ISensorFusionUnifiedTaskLookupDto } from '@scale/sensor-fusion-shared';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

import { openSelectedIfUrl } from '../helpers/url';
import SfuTaskOverview from './SfuTaskOverview';
import SfuTaskTaxonomyPanel from './SfuTaskTaxonomyPanel';
import SfuTaskActions from './SfuTaskActionsPanel';
import { DOUBLE_COLUMN_MIN_WIDTH } from '../constants';

type SfuTaskLookupViewProps = {
  extras: Partial<Extras>;
  task: ISensorFusionUnifiedTaskLookupDto;
};

const SfuTaskLookupView = ({ extras, task }: SfuTaskLookupViewProps) => {
  const hasPipelineNodes = !!task.pipelineNodes?.length;
  const hasLotsOfPipelineNodes = hasPipelineNodes && task.pipelineNodes!.length > 75;
  const { isRemoAdmin } = useRemoAdminCtx();

  const customerAuditHistoryProps = getLookupCustomerAuditHistoryProps({
    object: task,
    scalerAccess: extras.scalerAccess,
  });
  const internalAuditHistoryProps = getLookupInternalAuditHistoryProps({ object: task });

  // Handle window resize and 2 column layout
  const [width, setWidth] = useState<number | undefined>(undefined);
  const myRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const updateWidth = () => {
      setWidth(myRef?.current?.getBoundingClientRect().width);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const leftColumnElements = (
    <>
      <SfuTaskOverview task={task} />
      <SfuTaskActions task={task} />
      {task.taxonomy && (
        <HideableSection name="Taxonomy" containZoom>
          <SfuTaskTaxonomyPanel task={task} />
        </HideableSection>
      )}
      {!isRemoAdmin && (
        <HideableSection name="SFU Task JSON">
          <ReactJsonView
            src={task}
            name={false}
            theme="monokai"
            iconStyle="triangle"
            collapsed={1}
            onSelect={openSelectedIfUrl}
            collapseStringsAfterLength={120}
            groupArraysAfterLength={false}
            enableClipboard={true}
            displayObjectSize={false}
            displayDataTypes={false}
          />
        </HideableSection>
      )}
    </>
  );

  const rightColumnElements = (
    <>
      {hasPipelineNodes && (
        <HideableSection
          name="Pipeline Execution Overview Graph"
          forceHidden={hasLotsOfPipelineNodes}
          containZoom
        >
          <PipelineNodeGraph nodes={task.pipelineNodes!} executionId={task.pipelineExecutionId} />
        </HideableSection>
      )}

      {hasPipelineNodes && (
        <HideableSection name="Pipeline Execution History Graph" forceHidden>
          <PipelineHistoryGraph id={task._id}></PipelineHistoryGraph>
        </HideableSection>
      )}

      {customerAuditHistoryProps && (
        <HideableSection name="Customer Audit History">
          <CustomerAuditHistory {...customerAuditHistoryProps} />
        </HideableSection>
      )}

      {internalAuditHistoryProps && (
        <HideableSection name="Internal Audit History">
          <InternalAuditHistory {...internalAuditHistoryProps} />
        </HideableSection>
      )}
    </>
  );

  return (
    <div
      className="scaleui scale-tailwind scale-theme w-max-full w-full overflow-scroll px-8"
      ref={myRef}
    >
      <Head title={`SFU Task ${task._id} on ${task.project.name}`} />
      <div className="flex flex-col gap-4 max-w-full">
        <Typography.H1>Unified Sensor Fusion Task</Typography.H1>
        <WithCopy className="mx-1" copyText={task._id}>
          {task._id}
        </WithCopy>
      </div>
      {width && width >= DOUBLE_COLUMN_MIN_WIDTH ? (
        <div className="flex gap-4 min-w-0">
          <div className="flex flex-col gap-4 min-w-0 w-1/3 overflow-wrap-anywhere">
            {leftColumnElements}
          </div>
          <div className="flex flex-col gap-4 min-w-0 w-2/3 overflow-wrap-anywhere">
            {rightColumnElements}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 min-w-0 overflow-wrap-anywhere">
          {leftColumnElements}
          {rightColumnElements}
        </div>
      )}
      <style jsx>{`
        .overflow-wrap-anywhere {
          overflow-wrap: anywhere;
        }
      `}</style>
    </div>
  );
};

export default SfuTaskLookupView;
